import React from 'react'

import { FormRows } from '@acorns/web-components'
import * as Sentry from '@sentry/browser'
import { pathOr } from 'ramda'

import {
  APP_VERSION,
  EMAIL_FROM_MARKETING_SITE,
  OPTIMIZELY_EXPERIMENT_VARIATION_1,
  REDIRECTION_URL_KEY,
  VISITOR_ID_KEY,
} from 'app-constants'
import { EmailInput } from 'components/EmailInput'
import platform, { deviceType } from 'components/Trackers/platform'
import { ActionType, PageName } from 'generated/types'
import trackEvent from 'utils/analytics/trackEvent'
import { setCookies } from 'utils/auth'
import { sendToSentry } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getGeneralError } from 'utils/getGeneralError'
import {
  directToMobile,
  mocOnboardingEnabled,
  webToAppV2Experiment,
} from 'utils/Optimizely/features'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

import { generateErrorCodeMap } from '../errorCodeMap'
import { errorModal, generateUDID } from '../utils'
import { Link, StyledLabel } from './styled-components'

export type FormValues = {
  email: string
  password: string
  usResident: boolean
  partnerDisclosure?: boolean
}

declare global {
  interface Window {
    udid: string
  }
}

const parsed = removeDuplicateQueryParams(location.search)

export const isClickId = () => {
  if (window.location.search) {
    const params = removeDuplicateQueryParams(window.location.search)
    if (params.clickid) {
      return true
    } else {
      return false
    }
  } else {
    return false
  }
}

export const mapPropsToValues = ({ isPartnerReg, partner }) => {
  const emailFromMarketingSite = document.cookie
    .split(';')
    .filter(cookie => cookie.includes(EMAIL_FROM_MARKETING_SITE))
    .toString()
    .split('=')
    .pop()

  // Base form values
  function emailCheck() {
    if (partner) {
      return partner.user.email
    } else if (emailFromMarketingSite) {
      return emailFromMarketingSite
    } else if (parsed.email) {
      return parsed.email
    } else {
      return ''
    }
  }
  const values: FormValues = {
    email: emailCheck(),
    password: '',
    usResident: false,
  }

  // If this is partner reg, add default for disclosure property
  if (isPartnerReg || isClickId()) {
    values.partnerDisclosure = false
  }

  return values
}

export const handleFormSubmit = (values, { props, ...ownProps }) => {
  const { partner } = props

  const isMocEnabled = props.isFeatureEnabled(mocOnboardingEnabled)

  ownProps.setSubmitting(true)
  // Add Sentry breadcrumb in case there is an error
  Sentry.addBreadcrumb({
    message: 'User is attempting to create a new account.',
  })

  // Prepare data to be used in call to create user
  const user = {
    email: values.email,
    password: values.password,
  }

  const sourceAttributes = props.getSourceAttributes()
  let partnerName = null
  if (partner) {
    partnerName = partner.org.name
  } else if (isClickId()) {
    partnerName = 'impactradius'
  }

  const agreementIds = props.getAgreementIds(partnerName)

  // Provide authorization if registering through a partner
  // Default auth is the externalId and name as provided by partner
  const authorization = partner
    ? {
        authorizationUid: pathOr(partner.org.externalId, ['uid'], partner.org.authorization),
        authorizationProvider: pathOr(partner.org.name, ['provider'], partner.org.authorization),
        authorizationAccessToken: pathOr(null, ['accessToken'], partner.org.authorization),
        authorizationRefreshToken: pathOr(null, ['refreshToken'], partner.org.authorization),
        authorizationExpiresAt: pathOr(null, ['expiresAt'], partner.org.authorization),
      }
    : null

  const VISITOR_ID = document.cookie
    .split(';')
    .filter(cookie => cookie.includes(`${VISITOR_ID_KEY}`))
    .toString()
    .split('=')
    .pop()

  const codeGroup = props.referral?.s1 === 'Referrals' ? null : props.referral?.group
  const invitationCode = props.referral?.s1 === 'Referrals' ? null : props.referral?.code

  const data = {
    ...user,
    ...authorization,
    acceptanceDocumentVersionUuids: agreementIds,
    captchaToken: '',
    clientCredentialsToken: partner ? partner.org.clientCredentialsToken : null,
    codeGroup,
    invitationCode,
    sourceAttributes: sourceAttributes.attributes,
    sourceId: sourceAttributes.id,
    udid: generateUDID(props.env, VISITOR_ID),
    usResident: true,
    withBasicAccount: false,
  }

  const params = removeDuplicateQueryParams(location.search)
  props
    .createUser({ variables: data })
    .then(async ({ data: { createUser } }) => {
      const { redirectUrl, token, uuid } = createUser

      setCookies(
        {
          identityId: uuid,
          token,
        },
        user.email,
      )

      // @note: This feature will call the updateRoundUpProfile mutation to set
      // `roundUpsEnabled: true` on the associated user profile.
      if (props.isFeatureEnabled('enable_round_ups_after_create_user')) {
        await enableRoundUpsOnProfile(props)
      }

      // If response contained a redirction URL, store it
      if (redirectUrl) {
        window.sessionStorage.setItem(REDIRECTION_URL_KEY, redirectUrl)
      }

      // Add token to backend provider
      props.backend.defaults.headers.Authorization = `token ${token}`

      // Identify the user for subsequent tracking in interior pages
      window.analytics.identify(uuid, {
        app_version: APP_VERSION,
        partner: partnerName,
        funnel_type: props.flow,
        tier_price: props.tierPrice,
        source_id: sourceAttributes.id,
      })

      // Identify the user for error tracking
      Sentry.configureScope(scope => {
        scope.setUser({ id: uuid })
      })
    })
    .then(() => {
      if (props.flow === 'later' || props.flow === 'spend') {
        return props
          .updateRoundUpProfile({
            variables: {
              roundUpsEnabled: false,
            },
          })
          .catch(error => {
            sendToSentry(error, {
              breadcrumb: 'Failed to disable round ups on round up profile',
            })
          })
      }
    })
    .then(() => {
      trackEvent('Funnel Started', {
        eventData: {
          event: 'funnel_started',
          event_text: 'Funnel Started',
        },
        location,
        partner,
        flow: props.flow,
      })
    })
    .then(() => {
      // Update user profile data if user is registering through a partner
      /* istanbul ignore else */
      if (partner) {
        return props.updateUserProfile(user.email).then(() => {
          // Link funding source if account token is present
          if (partner.accountToken) {
            props.backend
              .post('v1/accounts', {
                account: {
                  account_token: partner.accountToken,
                },
              })
              .then(() => redirectToNextPage(props, isMocEnabled))
              .catch(error => {
                sendToSentry(error, {
                  breadcrumb:
                    'the user is coming from a partner but v1/accounts was unable to link a funding source.',
                })
                redirectToNextPage(props, isMocEnabled)
              })
          } else {
            redirectToNextPage(props, isMocEnabled)
          }
        })
      }
    })
    .then(() => {
      /* istanbul ignore else */
      if (!partner) {
        redirectToNextPage(props, isMocEnabled)
        ownProps.setSubmitting(false)
      }
    })
    .catch(error => {
      ownProps.setSubmitting(false)
      let errorCode

      if (error.graphQLErrors) {
        // Obtain error code from GraphQL response
        errorCode = pathOr(
          null,
          ['graphQLErrors', 0, 'extensions', 'response', 'body', 'errors', 0],
          error,
        )
      } else {
        // Obtain error from REST response
        errorCode = pathOr(null, ['response', 'data', 'errors', 0], error)
      }

      const errorCodeMap = generateErrorCodeMap(props, values)

      // This is for handling when backend sends us an error where captcha is required.
      // We don't want to rely on using a toggle to check backend because our client should
      // not be concerned with backend toggles.

      /* istanbul ignore else */
      if (errorCode && errorCode.toLowerCase().includes('captcha failed')) {
        ownProps.setSubmitting(false)
        return
      }

      if (errorCode && errorCodeMap.get(errorCode)) {
        const { title, message, actions } = errorCodeMap.get(errorCode)
        props.showErrorModal(errorModal(props, title, message, actions, errorCode))
      } else {
        const retryHandler = () => {
          props.closeErrorModal()
          ownProps.submitForm()
        }
        const { title, message, actions, image } = getGeneralError(error, props, retryHandler)
        props.showErrorModal(errorModal(props, title, message, actions, errorCode, image))
      }
      sendToSentry(error, { breadcrumb: 'Attempting to register a new user' })
    })
}

export const enableRoundUpsOnProfile = async props => {
  await props
    .updateRoundUpProfile({
      variables: {
        roundUpsEnabled: true,
      },
    })
    .catch(error => {
      sendToSentry(error, {
        breadcrumb: 'Failed to auto-enable round ups on round up profile',
      })
    })
}

const redirectToNextPage = (props, isMocEnabled) => {
  if (isMocEnabled) {
    props.isDynamicFunnelEnabled
      ? props.routeToPage(PageName.Signup, ActionType.Continue)
      : props.route({ from: FlowRoute.Signup, action: 'moc-onboarding' })
  } else {
    props.route({ from: FlowRoute.Signup, action: 'continue' })
  }
}

export const shouldRedirectToSuccessLander = (props): boolean => {
  const isLandingPageFeatureEnabled = checkIfFeatureEnabledWithUserInformation(props)
  const variation = props.optimizely.getVariation(webToAppV2Experiment)
  return isLandingPageFeatureEnabled && variation === OPTIMIZELY_EXPERIMENT_VARIATION_1
}

const checkIfFeatureEnabledWithUserInformation = ({ isFeatureEnabled }): boolean => {
  const optimizelyAttributes = {
    platform,
    deviceType,
  }
  return isFeatureEnabled(directToMobile, undefined, optimizelyAttributes)
}

const residencyLabel = openAgreementModal => (
  <StyledLabel>
    Yes, I am a U.S. Resident, 18 years or older, and agree to the{' '}
    <Link onClick={openAgreementModal('program_agreement')}>Program Agreement</Link>,{' '}
    <Link onClick={openAgreementModal('acorns_privacy_policy')}>Privacy Policy</Link> and{' '}
    <Link onClick={openAgreementModal('eft_agreement')}>Auto Debit Authorization</Link>.
  </StyledLabel>
)

const partnerDisclosureLabel = (openAgreementModal, partnerName) => {
  const agreementName = partnerName === 'PayPal' ? 'paypal_agreement' : 'partner_agreement'
  return (
    <StyledLabel>
      I have read the{' '}
      <Link onClick={openAgreementModal(agreementName)}>Partner Referral Disclosure</Link>
    </StyledLabel>
  )
}

export const emailAndPasswordRow: FormRows = [
  [
    {
      label: 'Email',
      name: 'email',
      type: EmailInput,
    },
    {
      label: 'Password',
      name: 'password',
      type: 'private',
      helperText: 'Password must contain at least 8 characters',
      placeholder: '••••••••',
      toggleIcon: true,
    },
  ],
]

export const disclosureRow = (openAgreementModal, partner) => {
  const rows: FormRows = [
    [
      {
        label: residencyLabel(openAgreementModal),
        name: 'usResident',
        sizes: { sm: 12, md: 12, xs: 12 },
        className: `signup-checkbox ${(partner || isClickId()) && 'resident-checkbox'}`,
        type: 'checkbox',
      },
    ],
  ]

  // If partner registration, add a row for the partner agreement checkbox
  if (partner || isClickId()) {
    rows.push([
      {
        label: partnerDisclosureLabel(
          openAgreementModal,
          pathOr('Impact Radius', ['org', 'name'], partner),
        ),
        name: 'partnerDisclosure',
        sizes: { sm: 12, md: 12, xs: 12 },
        className: 'signup-checkbox',
        type: 'checkbox',
      },
    ])
  }
  return rows
}
